import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { api } from "../api/client";
import { qk, useProposals } from "../api/queries";
import type { Proposal, ProposalStatus } from "../api/types";
import { useDialogs } from "../components/Dialogs";
import { Markdown } from "../components/Markdown";
import { MarkdownEditor } from "../components/MarkdownEditor";
import { Pill } from "../components/Pill";
import { RelativeTime } from "../components/RelativeTime";
import { ErrorBox, Spinner } from "../components/Spinner";
import { cx } from "../lib/format";

const TABS: { key: ProposalStatus; label: string }[] = [
  { key: "pending", label: "Pending" },
  { key: "accepted", label: "Accepted" },
  { key: "rejected", label: "Rejected" },
];

export function ProposalsPage() {
  const { pid = "" } = useParams();
  const [tab, setTab] = useState<ProposalStatus>("pending");
  const { data, isLoading, error } = useProposals(pid, tab);

  return (
    <div className="page page-medium">
      <header className="page-header">
        <div className="page-header-main">
          <h1 className="page-title">Proposals</h1>
          <div className="page-subtitle muted">
            Follow-up tasks proposed by agents. Accepting one adds it to the board.
          </div>
        </div>
      </header>
      <div className="tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            className={cx("tab", tab === t.key && "active")}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>
      {isLoading && <Spinner />}
      {error && <ErrorBox error={error} />}
      {data?.length === 0 && (
        <p className="muted empty">{tab === "pending" ? "No proposals waiting for review." : "Nothing here yet."}</p>
      )}
      <div className="proposal-list">
        {data?.map((p) => (
          <ProposalCard key={p.id} p={p} pid={pid} />
        ))}
      </div>
    </div>
  );
}

function ProposalCard({ p, pid }: { p: Proposal; pid: string }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(p.title);
  const [description, setDescription] = useState(p.description);
  const [criteria, setCriteria] = useState(p.acceptance_criteria);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { prompt } = useDialogs();

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: qk.proposals(pid) });
    qc.invalidateQueries({ queryKey: qk.tasks(pid) });
  };
  const accept = useMutation({
    meta: { inlineError: true },
    mutationFn: () =>
      api.acceptProposal(
        p.id,
        editing ? { title, description, acceptance_criteria: criteria } : {},
      ),
    onSuccess: (task) => {
      invalidate();
      navigate(`/p/${pid}/tasks/${task.id}`);
    },
  });
  const reject = useMutation({
    mutationFn: (reason: string) => api.rejectProposal(p.id, reason),
    onSettled: invalidate,
  });

  const onReject = async () => {
    const reason = await prompt({
      title: "Reject proposal",
      label: "Reason",
      placeholder: "Why this shouldn't become a task",
      multiline: true,
      confirmLabel: "Reject",
    });
    if (reason !== null) reject.mutate(reason);
  };

  return (
    <article className="proposal">
      <header className="proposal-header">
        {editing ? (
          <input className="input input-title-sm" value={title} onChange={(e) => setTitle(e.target.value)} />
        ) : (
          <h2 className="proposal-title">{p.title}</h2>
        )}
        <div className="muted small">
          Proposed from{" "}
          <Link to={`/p/${pid}/tasks/${p.from_task_id}`}>#{p.from_task_number}</Link> ·{" "}
          <RelativeTime iso={p.created_at} />
          {p.status !== "pending" && (
            <>
              {" · "}
              <Pill color={p.status === "accepted" ? "green" : "red"}>{p.status}</Pill>
              {p.decided_by_name && <> by {p.decided_by_name}</>}
            </>
          )}
        </div>
      </header>
      {editing ? (
        <div className="form-stack">
          <div className="field">
            <span className="field-label">Description</span>
            <MarkdownEditor value={description} onChange={setDescription} rows={6} />
          </div>
          <div className="field">
            <span className="field-label">Acceptance criteria</span>
            <MarkdownEditor value={criteria} onChange={setCriteria} rows={4} />
          </div>
        </div>
      ) : (
        <>
          {p.description && <Markdown>{p.description}</Markdown>}
          {p.acceptance_criteria && (
            <>
              <h4 className="section-label">Acceptance criteria</h4>
              <Markdown>{p.acceptance_criteria}</Markdown>
            </>
          )}
        </>
      )}
      {p.status === "rejected" && p.reason && (
        <div className="proposal-reason">
          <span className="muted small">Reason:</span> {p.reason}
        </div>
      )}
      {accept.error && <div className="error-box">{accept.error.message}</div>}
      {p.status === "pending" && (
        <div className="row-actions">
          <button className="btn btn-sm btn-ghost-danger" onClick={onReject} disabled={reject.isPending}>
            Reject
          </button>
          {editing ? (
            <button className="btn btn-sm" onClick={() => setEditing(false)}>
              Discard edits
            </button>
          ) : (
            <button className="btn btn-sm" onClick={() => setEditing(true)}>
              Edit
            </button>
          )}
          <button className="btn btn-sm btn-primary" onClick={() => accept.mutate()} disabled={accept.isPending}>
            {editing ? "Accept with edits" : "Accept"}
          </button>
        </div>
      )}
    </article>
  );
}
